import matchlib from './matchlib';
import * as GenericCalculator from './GenericCalculator';

import {
  OnChange,
  Type as Cache,
  Action as CacheAction,
  get as cacheGet,
} from './CalculationCache';

import App from './App';
import RoomPage from './RoomPage';

export type M = {
  Ratings: {
    input: { W: number[][] };
    output: number[];
  };
  PairValues: {
    input: { W: number[][], x: number[] };
    output: { i: number, j: number, value: number }[];
  };
};

export type Calculator = Calculator.SA['state'];

export function Calculator(): Calculator {
  return {
    Ratings: Cache<M['Ratings']>(),
    PairValues: Cache<M['PairValues']>(),
  };
}

export namespace Calculator {
  export type SA = GenericCalculator.SA<M>;
  export type MethodMap = M;

  export type Wanted<Method extends keyof M> = {
    method: Method,
    input: M[Method]['input'],
  };

  export type WantedList = Wanted<keyof M>[];

  export function reduce<Action extends SA['action']>(
    state: SA['state'],
    action: Action,
  ) {
    return GenericCalculator.reduce<M, Action>(state, action);
  }

  const onChangeRatings = OnChange<M['Ratings'], App.X>(
    (app: App) => {
      const page = App.CurrentPage(app);
      const room = App.CurrentRoom(app);

      if (page === null || room === null) {
        return [];
      }

      return (RoomPage
        .WantedCalculations(page, room, App.PreviousRoom(app, room.ref), app.calculator)
        .filter(w => w.method === 'Ratings')
        .map(w => w.input)
      );
    },
    matchlib.Ratings,
    (app: App) => app.calculator.Ratings,
    (action: CacheAction<M['Ratings']>) => ({
      type: 'Calculator',
      subAction: {
        method: 'Ratings',
        action,
      },
    }),
  );

  const onChangePairValues = OnChange<M['PairValues'], App.X>(
    (app: App) => {
      const page = App.CurrentPage(app);
      const room = App.CurrentRoom(app);

      if (page === null || room === null) {
        return [];
      }

      return (RoomPage
        .WantedCalculations(page, room, App.PreviousRoom(app, room.ref), app.calculator)
        .filter(w => w.method === 'PairValues')
        .map(w => w.input)
      );
    },
    matchlib.PairValues,
    (app: App) => app.calculator.PairValues,
    (action: CacheAction<M['PairValues']>) => ({
      type: 'Calculator',
      subAction: {
        method: 'PairValues',
        action,
      },
    }),
  );

  export function onChange(
    app: App,
    dispatch: (action: App.Action) => void
  ) {
    onChangeRatings(app, dispatch);
    onChangePairValues(app, dispatch);
  }

  export const get = cacheGet;
}

export default Calculator;



// WEBPACK FOOTER //
// ./src/state/Calculator.ts